/**
 * The chips under a parked question (D79/§8.7) — moved here from the shell
 * by D156, because the chips are part of the VIEW: a server game's client
 * cannot name a candidate it does not hold, so the host names them. D235
 * adds the actor's question: its options' labels are the chips.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import { type CommandSuggestion } from '@orpheus/api-contracts';
import { describeEntityRef, type EngineGameBundle, nameInSentence } from '@orpheus/engine-runtime';

/**
 * Turn a parked which-one question into ANSWER chips: each chip shows the
 * name as a sentence says it — `the {name}`, or the bare name when it
 * refuses the article (D226, E9: "Sylvain", never "the Sylvain") — and
 * submits the bare name — the player's answer, never a rebuilt command. Completion questions offer no chips (Mike's ruling: the
 * engine does not enumerate answers it has not validated — D39's spirit at
 * the UI); their surface is the persistent question text alone.
 *
 * @param question - The parked question
 * @param bundle - The bundle (candidate names)
 * @returns The chips to offer (empty for none)
 */
export const buildSuggestions = (
	question: NonNullable<RuntimeState['session']['pendingQuestion']>,
	bundle: EngineGameBundle,
): readonly CommandSuggestion[] => {
	// D179: a confirm's chips ARE its buttons — the words each submits are the
	// ones the engine folded into the question, so the transcript records the
	// click as the player's own answer (the R17 rule for every chip).
	if (question.kind === 'confirm') {
		// D212: only the RESTART gate's proceed is irreversible; a correction's
		// "yes" tries a command, which is what every other chip does.
		return [
			{
				label: question.proceed.label,
				submitValue: question.proceed.label,
				suggestionKey: 'confirm:proceed',
				...(question.act === 'restart' ? { tone: 'danger' as const } : {}),
			},
			{ label: question.decline.label, submitValue: question.decline.label, suggestionKey: 'confirm:decline' },
		];
	}

	// D235: an actor's question — the chips are the answers it offered, in its
	// order, each submitting its label as the player's words (the R17 rule
	// again): the engine matches the label whole, so what the chip says is
	// exactly what answers.
	if (question.kind === 'dialogue') {
		return question.options.map((option) => ({ label: option.label, submitValue: option.label, suggestionKey: `dialogue:${option.optionId}` }));
	}

	if (question.kind !== 'which_entity') {
		return [];
	}

	return question.candidates.map((candidate) => {
		const candidateName = describeEntityRef(bundle, candidate.entityRef as Parameters<typeof describeEntityRef>[1]);

		return { label: nameInSentence(bundle, candidateName), submitValue: candidateName, suggestionKey: candidate.entityRef };
	});
};
